/**
 * PushNotificationPreview - iOS-style notification preview
 *
 * Pure display component that renders a notification template preview
 * with template variable substitution and unmapped variable highlighting.
 */

import React from "react";
import type { NotificationTemplate } from "@/features/notification-templates/types";
import { useI18n } from "@/i18n";

interface PushNotificationPreviewProps {
  template: NotificationTemplate;
  /** Set of variable names that have been mapped to upstream sources */
  mappedVariableNames: Set<string>;
}

/**
 * Render text with {variable} placeholders substituted:
 * - Mapped variables: show default value in blue
 * - Unmapped variables: show variable name in orange
 */
function renderWithVariables(
  text: string,
  template: NotificationTemplate,
  mappedVariableNames: Set<string>,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // Match {variableName} placeholders
  const regex = /\{(\w+)\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const varName = match[1];
    const isMapped = mappedVariableNames.has(varName);
    const variable = template.variables.find((v) => v.name === varName);
    const displayText = isMapped ? variable?.default || varName : varName;

    parts.push(
      <span
        key={`${varName}-${match.index}`}
        className={`inline-block px-1 rounded text-xs font-medium ${
          isMapped
            ? "bg-blue-100 text-blue-700"
            : "bg-orange-100 text-orange-700"
        }`}
      >
        {displayText}
      </span>,
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export const PushNotificationPreview: React.FC<
  PushNotificationPreviewProps
> = ({ template, mappedVariableNames }) => {
  const { t } = useI18n();
  const { content } = template;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {t("imageWorkflows.pushTarget.previewTitle")}
      </label>

      {/* iOS-style notification banner */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-3">
          {/* App icon + app name row */}
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-rose-400 to-purple-500 flex items-center justify-center">
              <span className="text-white text-[8px] font-bold">D</span>
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wide">
              {template.display_name}
            </span>
          </div>

          {/* Title */}
          <div className="text-sm font-semibold text-gray-900 mb-0.5">
            {renderWithVariables(content.title, template, mappedVariableNames)}
          </div>

          {/* Body */}
          <div className="text-sm text-gray-600">
            {renderWithVariables(content.body, template, mappedVariableNames)}
          </div>
        </div>

        {/* Action badge */}
        {content.action && (
          <div className="px-3 pb-2">
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <span>{t("imageWorkflows.pushTarget.previewAction")}:</span>
              <span className="font-medium text-gray-700">
                {content.action_label || content.action}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushNotificationPreview;
